import { Alert, Button, Sentinel, Spinner } from '@ibsys/kit';
import { FC, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';

type Props = {
  /** Маркер подгрузки из `useGuardedInfiniteScroll`: его появление на экране запрашивает порцию */
  sentinelRef: RefObject<HTMLDivElement | null>;
  isFetchingNextPage: boolean;
  /** Порция не пришла — автоподгрузка остановлена до повтора */
  isAutoLoadStopped: boolean;
  /** Сообщение об отказе, если общее «не удалось догрузить» не называет предмет ленты */
  failureMessage?: string;
  onRetry: () => void;
  className?: string;
};

/**
 * Маркер подгрузки ленты вместе с признаком идущей попытки и отказом.
 *
 * Отказ показывается и объясняется одинаково во всех лентах, поэтому живёт рядом с
 * `useGuardedInfiniteScroll`, а не собирается каждым списком заново: иначе очередной потребитель
 * получает свой вид отказа, а исправление в одном месте не доходит до остальных.
 */
export const InfiniteScrollSentinel: FC<Props> = ({
  sentinelRef,
  isFetchingNextPage,
  isAutoLoadStopped,
  failureMessage,
  onRetry,
  className,
}) => {
  const { t } = useTranslation(['common']);

  return (
    <Sentinel ref={sentinelRef} className={cn('w-full', className)}>
      {isFetchingNextPage && (
        <div className="w-full flex justify-center py-6">
          <Spinner className="size-8" />
        </div>
      )}
      {isAutoLoadStopped && (
        <div className="w-full flex flex-col items-center gap-2 py-6">
          <Alert type="warning" title={failureMessage ?? t('common:loadMoreFailed')} />
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t('common:retry')}
          </Button>
        </div>
      )}
    </Sentinel>
  );
};
